/* wwm-cart-qty-dropdown.js
 * Custom, fully-styled quantity dropdown for cart line items. Replaces the
 * stepper visually while re-using the theme's cart update pipeline: selecting a
 * value dispatches the same 'quantity-selector:update' event the cart listens
 * for, and a hidden input keeps the state the cart component reads for sync and
 * error rollback.
 *
 * Morph-resilient: the cart re-renders via morphSection on every update, which
 * can replace this element's children. So we DON'T cache child refs — they're
 * looked up fresh via getters — and all listeners are delegated onto the custom
 * element itself (which persists), never onto children that morph may swap out.
 */
class WwmCartQtyDropdown extends HTMLElement {
  get input() { return this.querySelector('input[data-cart-quantity]'); }
  get toggle() { return this.querySelector('.wwm-qty__toggle'); }
  get menu() { return this.querySelector('.wwm-qty__menu'); }
  get valueEl() { return this.querySelector('.wwm-qty__value'); }
  get line() { return Number(this.dataset.line) || undefined; }

  connectedCallback() {
    if (this._bound) return;
    this._bound = true;

    // Delegated listeners on the persistent custom element.
    this.addEventListener('click', (e) => {
      if (e.target.closest('.wwm-qty__toggle')) {
        e.stopPropagation();
        const menu = this.menu;
        this.setOpen(menu ? menu.hasAttribute('hidden') : false);
        return;
      }
      const li = e.target.closest('[data-value]');
      if (li && this.menu && this.menu.contains(li)) this.select(Number(li.dataset.value));
    });

    this.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { this.setOpen(false); this.toggle?.focus(); }
    });

    this._onDocClick = (e) => { if (!this.contains(e.target)) this.setOpen(false); };
    document.addEventListener('click', this._onDocClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._onDocClick);
    this._detachReflow();
  }

  setOpen(open) {
    const menu = this.menu;
    if (!menu) return;
    menu.toggleAttribute('hidden', !open);
    this.toggle?.setAttribute('aria-expanded', open ? 'true' : 'false');
    this.classList.toggle('is-open', open);

    if (open) {
      this.positionMenu();
      const sel = menu.querySelector('.is-selected');
      if (sel) menu.scrollTop = Math.max(0, sel.offsetTop - menu.clientHeight / 2 + sel.offsetHeight / 2);
      this._attachReflow();
    } else {
      this._detachReflow();
    }
  }

  // Close on scroll/resize of ANYTHING OUTSIDE the menu (the fixed menu doesn't
  // follow the drawer). Scrolling the menu's own option list must NOT close it.
  _attachReflow() {
    this._detachReflow();
    this._onReflow = (e) => {
      if (e && e.type === 'scroll' && this.menu && this.menu.contains(e.target)) return;
      this.setOpen(false);
    };
    window.addEventListener('scroll', this._onReflow, true);
    window.addEventListener('resize', this._onReflow);
  }

  _detachReflow() {
    if (!this._onReflow) return;
    window.removeEventListener('scroll', this._onReflow, true);
    window.removeEventListener('resize', this._onReflow);
    this._onReflow = null;
  }

  select(value) {
    this.setOpen(false);
    const input = this.input;
    if (!input || String(value) === String(input.value)) return;
    input.value = String(value);
    this.updateDisplay(value);
    this.dispatchEvent(new CustomEvent('quantity-selector:update', {
      bubbles: true,
      detail: { quantity: value, cartLine: this.line },
    }));
  }

  updateDisplay(value) {
    const v = this.valueEl;
    if (v) v.textContent = value;
    this.menu?.querySelectorAll('[data-value]').forEach((li) => {
      li.classList.toggle('is-selected', Number(li.dataset.value) === Number(value));
    });
  }

  positionMenu() {
    const toggle = this.toggle;
    const menu = this.menu;
    if (!toggle || !menu) return;

    const r = toggle.getBoundingClientRect();
    const gap = 6;
    const cap = 280;

    // The cart drawer <dialog> keeps a transform (slide animation, fill:forwards),
    // making it the containing block for our position:fixed menu — offset by its
    // rect so coordinates are relative to it, not the raw viewport.
    const cb = this.closest('dialog');
    const o = cb ? cb.getBoundingClientRect() : { left: 0, top: 0 };

    menu.style.left = r.left - o.left + 'px';
    menu.style.maxHeight = '';

    const spaceBelow = window.innerHeight - r.bottom - 8;
    const spaceAbove = r.top - 8;
    const natural = menu.scrollHeight;

    if (spaceBelow >= Math.min(natural, cap) || spaceBelow >= spaceAbove) {
      menu.style.top = r.bottom - o.top + gap + 'px';
      menu.style.maxHeight = Math.min(natural, spaceBelow, cap) + 'px';
    } else {
      const h = Math.min(natural, spaceAbove, cap);
      menu.style.top = r.top - o.top - gap - h + 'px';
      menu.style.maxHeight = h + 'px';
    }
  }
}

if (!customElements.get('wwm-cart-qty-dropdown')) {
  customElements.define('wwm-cart-qty-dropdown', WwmCartQtyDropdown);
}
